import { CompanyType, PathsV1CompanySearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useCustomerBulkDeletePreviewQuery } from "@/common/api/customer/use-customer-bulk-delete-preview.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CircleAlertIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isOperationalCustomerCompany } from "../../company/company-identifiers";
import {
  countCustomerCompanySelections,
  hasCustomerCompanySelection,
  type CustomerCompanyAction,
  type CustomerCompanySelection,
} from "../_helpers/customer-company-selection";
import { getCustomerCompanyActionDialogContent } from "./_helpers/customer-company-action-dialog-content";
import { CustomerCompanySelectionList } from "./customer-company-selection-list";

type CustomerCompanySelectionDialogProps = {
  action: CustomerCompanyAction | null;
  selection: CustomerCompanySelection;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onToggleCompany: (companyUuid: string) => void;
  onToggleUnassignedCustomers: () => void;
};

export function CustomerCompanySelectionDialog({
  action,
  selection,
  isPending,
  onClose,
  onConfirm,
  onToggleCompany,
  onToggleUnassignedCustomers,
}: CustomerCompanySelectionDialogProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { pageSize, createPaginationState } = useComboboxPagination();
  const {
    data: companies,
    error: companySearchError,
    isPending: areCompaniesLoading,
  } = useCompanySearchQuery({
    enabled: action !== null,
    options: {
      parameters: {
        pageSize,
        extend: [PathsV1CompanySearchPostParametersQueryExtend.CompanyIdentifiers],
      },
      body: { type: [CompanyType.Alphartis] },
    },
  });
  const companyOptions = useMemo(
    () =>
      (companies?.data ?? [])
        .filter(isOperationalCustomerCompany)
        .filter((company) => company.uuid && company.name)
        .map((company) => ({ uuid: company.uuid, name: company.name })),
    [companies],
  );
  const companyPagination = createPaginationState(
    companies?.meta?.total,
    companies?.data?.length ?? 0,
    areCompaniesLoading,
  );
  const content = action ? getCustomerCompanyActionDialogContent(action) : null;
  const hasSelection = hasCustomerCompanySelection(selection);
  const isBulkDelete = action?.type === "bulk-delete";
  const {
    data: bulkDeletePreview,
    isFetching: isBulkDeletePreviewFetching,
    isError: isBulkDeletePreviewError,
  } = useCustomerBulkDeletePreviewQuery({
    body: selection,
    enabled: isBulkDelete && hasSelection,
  });

  if (!content) {
    return null;
  }

  const selectionLabel = t(content.selectionLabelKey);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent onClose={onClose} className="grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0">
        <DialogHeader className="px-4 pt-4 sm:px-5 sm:pt-5">
          <DialogTitle>{t(content.titleKey)}</DialogTitle>
          <DialogDescription>{t(content.descriptionKey)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto px-4 py-3 sm:px-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium">{selectionLabel}</p>
            <p className="text-muted-foreground text-sm">
              {t("masterdata:CustomerCompanySelectionCount", {
                count: countCustomerCompanySelections(selection),
              })}
            </p>
          </div>
          <CustomerCompanySelectionList
            ariaLabel={selectionLabel}
            companies={companyOptions}
            selection={selection}
            hasError={Boolean(companySearchError)}
            isLoading={companyPagination.isLoading}
            hasMore={companyPagination.hasMore}
            isLoadingMore={companyPagination.isLoadingMore}
            onLoadMore={companyPagination.onLoadMore}
            onToggleCompany={onToggleCompany}
            onToggleUnassignedCustomers={onToggleUnassignedCustomers}
          />
          {isBulkDelete && hasSelection && bulkDeletePreview && (
            <div className="space-y-2 text-sm">
              <p>
                {t("masterdata:CustomerBulkDeletePreview", {
                  count: bulkDeletePreview.deletableCustomerCount,
                })}
              </p>
              {bulkDeletePreview.skippedSharedCustomerCount > 0 && (
                <Alert variant="warning">
                  <CircleAlertIcon />
                  <AlertTitle>
                    {t("masterdata:CustomerBulkDeleteSharedCustomersSkipped", {
                      count: bulkDeletePreview.skippedSharedCustomerCount,
                    })}
                  </AlertTitle>
                  <AlertDescription>
                    <p>{t("masterdata:CustomerBulkDeleteSelectAffectedCompanies")}</p>
                    <ul className="list-disc pl-4">
                      {bulkDeletePreview.affectedCompanies.map((company) => (
                        <li key={company.uuid}>
                          {company.name} ({company.customerCount})
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          {isBulkDelete && hasSelection && isBulkDeletePreviewError && (
            <Alert variant="error">
              <CircleAlertIcon />
              <AlertDescription>{t("masterdata:CustomerBulkDeletePreviewError")}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter className="bg-background mx-0 border-t px-4 py-4 sm:px-5">
          <Button variant={ButtonVariant.Outlined} colorVariant={ButtonColorVariant.Secondary} onClick={onClose}>
            {t("common:Cancel")}
          </Button>
          <Button
            colorVariant={content.submitColor}
            disabled={
              !hasSelection ||
              isPending ||
              (isBulkDelete && (isBulkDeletePreviewFetching || isBulkDeletePreviewError || !bulkDeletePreview))
            }
            onClick={onConfirm}
          >
            {t(content.submitKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
